import React, { useState } from "react";
import NavBar from "../components/navbar";
import {
  Button,
  CircularProgress,
  Container,
  Divider,
  Fab,
  Paper,
} from "@mui/material";
import PositionsTable, { PositionTableRow } from "./PositionsTable";
import { IMaskInput } from "react-imask";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { LoadPositionList } from "../../../domain/usecases/load-position-list";
import { Position } from "../../../domain/models/position-model";
import { LoadProductListByManyCodes } from "../../../domain/usecases/load-product-list-by-many-codes";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "../hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Inventory } from "../../../domain/models/inventory";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

export const TextMaskCustom = React.forwardRef<HTMLElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="00.00.00.00"
        inputRef={ref}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

type Props = {
  loadPosition: LoadPositionList;
  loadProductList: LoadProductListByManyCodes;
};

export default function Positions({ loadPosition, loadProductList }: Props) {
  const getCurrentWarehouse = useSelector<
    RootState,
    () => { id: number; name: string }
  >((state) => state.getCurrentWarehouse);
  const [positions, setPositions] = useState<Position[]>([]);
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const handleError = useErrorHandler((error) => {
    alert("Erro Inesperado");
  });

  const navigate = useNavigate();

  const handleClick = async () => {
    const warehouse = getCurrentWarehouse();
    const [street, building, level, apartment] = code.split(".");
    const params: any = {
      warehouseId: warehouse.id,
      filterOptions: {
        includeInventory: true,
      },
    };
    if (street) {
      params.filterOptions.street = Number(street);
    }
    if (building) {
      params.filterOptions.building = Number(building);
    }
    if (level) {
      params.filterOptions.level = Number(level);
    }
    if (apartment) {
      params.filterOptions.apartment = Number(apartment);
    }
    setIsLoading(true);
    try {
      const res = await loadPosition.load(params);

      if (res) {
        const aggregated = res.map((position) => {
          const aggregatedInventories = position.inventories?.reduce(
            (acc: Record<string, Inventory>, obj) => {
              const key = obj.item?.code + "-" + obj.item?.batch; // Concatenate code and batch to create a unique key
              if (!acc[key]) {
                acc[key] = obj;
              } else {
                acc[key] = {
                  ...acc[key],
                  amount: acc[key].amount + obj.amount,
                }; // Accumulate the sum for each unique key
              }
              return acc;
            },
            {}
          );
          const inventories = Object.values(aggregatedInventories || {});
          return { ...position, inventories };
        });
        const codes: string[] = [];
        aggregated.forEach((position) => {
          position.inventories?.forEach((inventory) => {
            codes.push(inventory!.item!.code);
          });
        });
        const products = await loadProductList.load(Array.from(new Set(codes)));
        aggregated.forEach((position) => {
          position.inventories?.forEach((inventory) => {
            const match = products.find(
              (product) => product.eanCode === inventory.item?.code
            );
            inventory.item!.description = match?.description;
            inventory.item!.packageAmount = match?.packageAmount;
            inventory.item!.reference = match?.reference;
            inventory.item!.unit = match?.unit!;
          });
        });
        setPositions(aggregated);
      }
    } catch (error) {
      console.log(error);
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const data: PositionTableRow[] = [];

  positions.forEach((position) => {
    position.inventories?.forEach((inventory) => {
      const row: Partial<PositionTableRow> = {};
      row.id = inventory.id?.toString();
      row.position = `${position.street}.${position.building}.${position.level}.${position.apartment}`;
      row.description = inventory.item?.description;
      row.reference = inventory.item?.reference;
      row.ean = inventory?.item?.code;
      row.amount = inventory.amount.toString();
      row.batch = inventory?.item?.batch?.trim();
      row.packageAmount = inventory.item?.packageAmount;
      row.unit = inventory.item?.unit;
      data.push(row as PositionTableRow);
    });
  });

  return (
    <>
      <NavBar title="Posições" />
      <Container
        sx={{
          my: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FormControl>
          <InputLabel htmlFor="formatted-text-mask-input">Posição</InputLabel>
          <Input
            value={code}
            onChange={(e: any) => {
              setCode(e.target.value);
            }}
            name="textmask"
            id="formatted-text-mask-input"
            inputComponent={TextMaskCustom as any}
          />
        </FormControl>
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: "absolute", right: "100px" }}
          onClick={() => {
            navigate("/positions/new");
          }}
        >
          <AddIcon />
        </Fab>
      </Container>
      <Divider>
        <Button
          variant="contained"
          size="large"
          onClick={handleClick}
          startIcon={
            isLoading && <CircularProgress color="secondary" size={20} />
          }
        >
          Pesquisar
        </Button>
      </Divider>
      <Container sx={{ my: "50px" }}>
        {positions.length > 0 && (
          <Paper>
            <PositionsTable data={data} />
          </Paper>
        )}
      </Container>
    </>
  );
}
